/**
 * RetailerTree — the Retailer › Sub-retailer list for the Retailers workspace.
 *
 * The model is one level deep (a sub-retailer is a retailer with a parent), so
 * the tree is parents with their children nested. Counts on a parent roll its
 * sub-retailers up, so "Home Hardware 14 · 6" is the whole chain. Admin-only (English).
 */

import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Building2, MapPin, Users, Inbox } from 'lucide-react';
import type { RetailerTreeNode } from '../../services/adminRetailerTreeApi';

export type TreeSelection = number | 'unassigned' | null;

export interface TreeBranch { node: RetailerTreeNode; children: RetailerTreeNode[] }

/** Parents (and orphans whose parent is missing) with their children, each sorted by name. */
export function buildTree(nodes: RetailerTreeNode[]): TreeBranch[] {
  const byId = new Map(nodes.map(n => [n.id, n]));
  const kids = new Map<number, RetailerTreeNode[]>();
  const roots: RetailerTreeNode[] = [];
  for (const n of nodes) {
    if (n.parentRetailerId !== null && byId.has(n.parentRetailerId)) {
      const list = kids.get(n.parentRetailerId) ?? [];
      list.push(n);
      kids.set(n.parentRetailerId, list);
    } else {
      roots.push(n);
    }
  }
  const byName = (a: RetailerTreeNode, b: RetailerTreeNode) => a.name.localeCompare(b.name);
  roots.sort(byName);
  return roots.map(node => ({ node, children: (kids.get(node.id) ?? []).sort(byName) }));
}

/** A node's counts plus its sub-retailers'. */
export function rolledCounts(branch: TreeBranch): RetailerTreeNode['counts'] {
  if (branch.node.familyCounts) return branch.node.familyCounts;
  return branch.children.reduce((acc, c) => ({
    locations: acc.locations + c.counts.locations,
    people: acc.people + c.counts.people,
    waiting: acc.waiting + c.counts.waiting,
  }), { ...branch.node.counts });
}

interface Props {
  nodes: RetailerTreeNode[];
  selected: TreeSelection;
  onSelect: (sel: TreeSelection) => void;
  /** Text filter over retailer names (a matching child keeps its parent visible). */
  filter?: string;
  unassigned?: { locations: number; people: number };
  /** Retailer #1 (Trusscore MSRP) is hidden unless it carries data or this is on. */
  showTrusscore?: boolean;
}

const Counts: React.FC<{ counts: RetailerTreeNode['counts'] }> = ({ counts }) => (
  <span className="d-inline-flex align-items-center gap-2 small text-muted text-nowrap">
    <span className="d-inline-flex align-items-center gap-1" title="Locations"><MapPin size={12} /> {counts.locations}</span>
    <span className="d-inline-flex align-items-center gap-1" title="People"><Users size={12} /> {counts.people}</span>
    {counts.waiting > 0 && <span className="badge text-bg-warning" title="Waiting for approval">{counts.waiting}</span>}
  </span>
);

export const RetailerTree: React.FC<Props> = ({ nodes, selected, onSelect, filter = '', unassigned, showTrusscore = false }) => {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const q = filter.trim().toLowerCase();

  const branches = useMemo(() => {
    const visible = nodes.filter(n => showTrusscore || n.id !== 1 || n.counts.locations > 0 || n.counts.people > 0 || n.counts.waiting > 0);
    const tree = buildTree(visible);
    if (!q) return tree;
    return tree
      .map(b => {
        const parentHit = b.node.name.toLowerCase().includes(q);
        const children = parentHit ? b.children : b.children.filter(c => c.name.toLowerCase().includes(q));
        return parentHit || children.length ? { node: b.node, children } : null;
      })
      .filter((b): b is TreeBranch => b !== null);
  }, [nodes, q, showTrusscore]);

  const toggle = (id: number) => setCollapsed(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const rowCls = (active: boolean) => `list-group-item list-group-item-action d-flex align-items-center gap-2 py-2 ${active ? 'active' : ''}`;

  return (
    <div className="list-group list-group-flush" role="tree" aria-label="Retailers">
      {unassigned && (
        <button type="button" role="treeitem" aria-selected={selected === 'unassigned'}
          className={rowCls(selected === 'unassigned') + ' border-bottom mb-2'}
          onClick={() => onSelect('unassigned')}>
          <span style={{ width: 14 }} />
          <Inbox size={14} className={selected === 'unassigned' ? 'text-white' : 'text-warning'} />
          <span className="flex-grow-1 fw-semibold">Unassigned</span>
          <span className={`d-inline-flex align-items-center gap-2 small text-nowrap ${selected === 'unassigned' ? 'text-white-50' : 'text-muted'}`}>
            <span className="d-inline-flex align-items-center gap-1" title="Stores with no retailer"><MapPin size={12} /> {unassigned.locations}</span>
            {unassigned.people > 0 && <span className="badge text-bg-warning" title="People waiting with no retailer picked">{unassigned.people}</span>}
          </span>
        </button>
      )}
      {branches.length === 0 && <div className="list-group-item text-muted small">No retailer matches.</div>}
      {branches.map(b => {
        const open = !collapsed.has(b.node.id) || q !== '';
        const active = selected === b.node.id;
        return (
          <React.Fragment key={b.node.id}>
            <button type="button" role="treeitem" aria-expanded={b.children.length ? open : undefined} aria-selected={active}
              className={rowCls(active) + (b.node.isActive ? '' : ' text-muted')}
              onClick={() => onSelect(b.node.id)}>
              {b.children.length > 0 ? (
                <span role="button" tabIndex={-1} className={`d-inline-flex ${active ? 'text-white' : 'text-muted'}`} title={open ? 'Collapse' : 'Expand'}
                  onClick={e => { e.stopPropagation(); toggle(b.node.id); }}>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              ) : <span style={{ width: 14 }} />}
              <Building2 size={14} className={active ? 'text-white' : 'text-secondary'} />
              <span className="flex-grow-1 text-truncate fw-semibold">{b.node.name}{!b.node.isActive && <span className="badge text-bg-secondary ms-1">paused</span>}</span>
              <span className={active ? 'text-white-50' : ''}><Counts counts={rolledCounts(b)} /></span>
            </button>
            {open && b.children.map(c => {
              const cActive = selected === c.id;
              return (
                <button key={c.id} type="button" role="treeitem" aria-selected={cActive}
                  className={rowCls(cActive) + (c.isActive ? '' : ' text-muted')} style={{ paddingLeft: '2.4rem' }}
                  onClick={() => onSelect(c.id)}>
                  <span className={`small ${cActive ? 'text-white-50' : 'text-muted'}`} aria-hidden="true">↳</span>
                  <span className="flex-grow-1 text-truncate">{c.name}{c.pricingSource === 'parent' && <span className={`badge ms-1 ${cActive ? 'text-bg-light' : 'text-bg-info'}`} title="Uses the parent's price book">inherits pricing</span>}{!c.isActive && <span className="badge text-bg-secondary ms-1">paused</span>}</span>
                  <span className={cActive ? 'text-white-50' : ''}><Counts counts={c.counts} /></span>
                </button>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default RetailerTree;
